import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineChevronUp,
  HiOutlineChevronDown, HiOutlineX, HiOutlinePhotograph,
} from 'react-icons/hi';

// Shared layout pieces for the SuperAdmin → Landing Page screens (Blog,
// Videos, Other Products), so the three pages look and behave the same.

/** Page header: icon tile, title, description and the "New …" button. */
export const ContentPageHeader = ({ icon: Icon, title, description, actionLabel, onAction }) => (
  <div className="flex flex-wrap items-start justify-between gap-4">
    <div className="flex items-start gap-3 min-w-0 flex-1 basis-72">
      <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    <button
      type="button"
      onClick={onAction}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                 hover:bg-primary-600 shadow-lg shadow-primary-500/25 whitespace-nowrap"
    >
      <HiOutlinePlus className="w-4 h-4" />
      {actionLabel}
    </button>
  </div>
);

/** Total / Live / Hidden counts. */
export const ContentStats = ({ items, noun }) => {
  const live = items.filter((i) => i.isActive).length;
  const tiles = [
    { label: `Total ${noun}`, value: items.length, color: 'text-gray-900' },
    { label: 'Live on website', value: live, color: 'text-green-600' },
    { label: 'Hidden', value: items.length - live, color: 'text-gray-500' },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className={`text-2xl font-bold ${t.color}`}>{t.value}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t.label}</p>
        </div>
      ))}
    </div>
  );
};

/** Card holding the filter bar and the list. */
export const ContentListCard = ({ filters, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    {filters && (
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
        {filters}
      </div>
    )}
    {children}
  </div>
);

export const ContentEmpty = ({ icon: Icon, title, hint, actionLabel, onAction }) => (
  <div className="text-center py-14 px-4">
    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
      <Icon className="w-7 h-7 text-gray-400" />
    </div>
    <p className="text-sm font-semibold text-gray-800">{title}</p>
    {hint && <p className="text-sm text-gray-500 mt-1">{hint}</p>}
    {onAction && (
      <button
        type="button"
        onClick={onAction}
        className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-primary-200 text-primary-600 text-sm font-medium hover:bg-primary-50"
      >
        <HiOutlinePlus className="w-4 h-4" /> {actionLabel}
      </button>
    )}
  </div>
);

/** Square/wide thumbnail with an icon fallback. */
export const ContentThumb = ({ src, icon: Icon, wide = false }) => (
  <div className={clsx(
    'rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 bg-gray-50 flex items-center justify-center',
    wide ? 'w-16 h-12 sm:w-24 sm:h-16' : 'w-12 h-12',
  )}>
    {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <Icon className="w-6 h-6 text-gray-300" />}
  </div>
);

/** Move up / down. Hidden while filtering — a filtered subset can't be reordered safely. */
export const ReorderButtons = ({ isFirst, isLast, onUp, onDown, disabled }) => (
  <div className="flex flex-col flex-shrink-0">
    <button type="button" title="Move up" disabled={isFirst || disabled} onClick={onUp}
      className="p-0.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-25 disabled:hover:bg-transparent">
      <HiOutlineChevronUp className="w-4 h-4" />
    </button>
    <button type="button" title="Move down" disabled={isLast || disabled} onClick={onDown}
      className="p-0.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-25 disabled:hover:bg-transparent">
      <HiOutlineChevronDown className="w-4 h-4" />
    </button>
  </div>
);

/** Live / Hidden switch. */
export const LiveToggle = ({ isActive, onToggle, disabled }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={disabled}
    title={isActive ? 'Shown on the website — click to hide' : 'Hidden from the website — click to show'}
    className="inline-flex items-center gap-2 disabled:opacity-60"
  >
    <span className={clsx('relative inline-flex h-5 w-9 rounded-full transition-colors', isActive ? 'bg-green-500' : 'bg-gray-300')}>
      <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform', isActive ? 'translate-x-4' : 'translate-x-0.5')} />
    </span>
    <span className={clsx('text-xs font-semibold w-11 text-left', isActive ? 'text-green-700' : 'text-gray-500')}>
      {isActive ? 'Live' : 'Hidden'}
    </span>
  </button>
);

/** Edit + delete (with confirmation). */
export const RowActions = ({ name, onEdit, onDelete }) => (
  <div className="flex items-center gap-1">
    <button type="button" title="Edit" onClick={onEdit}
      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700">
      <HiOutlinePencil className="w-4 h-4" />
    </button>
    <button
      type="button"
      title="Delete"
      onClick={() => { if (window.confirm(`Delete "${name}"? This cannot be undone.`)) onDelete(); }}
      className="p-2 rounded-lg text-red-500 hover:bg-red-50"
    >
      <HiOutlineTrash className="w-4 h-4" />
    </button>
  </div>
);

/** One list row: [reorder] thumb | text | toggle + actions (wraps under on phones). */
export const ContentRow = ({ reorder, thumb, title, meta, description, isActive, controls }) => (
  <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 px-4 py-3 hover:bg-gray-50/60">
    <div className="flex items-center gap-3 min-w-0 flex-1 basis-full sm:basis-auto">
      {reorder}
      {thumb}
      <div className="min-w-0 flex-1">
        <p className={clsx('text-sm font-semibold line-clamp-2 sm:line-clamp-1 break-words', isActive ? 'text-gray-900' : 'text-gray-400')}>{title}</p>
        {meta && <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">{meta}</div>}
        {description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{description}</p>}
      </div>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0 ml-auto">{controls}</div>
  </div>
);

export const MetaDot = ({ className }) => <span className={clsx('text-gray-300', className)}>•</span>;

// ── Add / edit form ────────────────────────────────────────────────────────

/** Modal with a fixed header and footer and a scrolling body. Esc closes it. */
export const ContentModal = ({ title, subtitle, onClose, onSave, saveLabel = 'Save', saving, canSave = true, wide = false, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative bg-white rounded-2xl shadow-2xl w-full flex flex-col max-h-[92vh]', wide ? 'max-w-3xl' : 'max-w-xl')}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" title="Close">
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">{children}</div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/** Labelled form field. */
export const Field = ({ label, required, hint, children, className }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

/** Image / file picker with a preview of the new or current file. */
export const FilePicker = ({ accept, file, currentUrl, currentName, onChange, hint, image = true }) => {
  const previewUrl = useMemo(() => (file && image ? URL.createObjectURL(file) : null), [file, image]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const shown = previewUrl || (image ? currentUrl : null);

  return (
    <div className="flex items-center gap-4">
      {image && (
        <div className="w-28 h-20 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
          {shown ? <img src={shown} alt="" className="w-full h-full object-cover" /> : <HiOutlinePhotograph className="w-7 h-7 text-gray-300" />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
          {file || currentUrl || currentName ? 'Change file' : 'Choose file'}
          <input type="file" accept={accept} className="sr-only" onChange={(e) => onChange(e.target.files?.[0] || null)} />
        </label>
        <p className="text-xs text-gray-500 mt-1.5 truncate">
          {file ? file.name : currentName || (currentUrl ? 'Current file kept unless you choose a new one' : 'No file chosen')}
        </p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
};
